import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import SettingsPageLayout from './SettingsPageLayout';
import toast from '../shared/Toast';
import {
  TRADES, TRADE_IDS, PALETTE_NAMES,
  getDiaryDemo, setDiaryDemo, clearDiaryDemo, demoSummary,
} from '../demo/diaryDemo';
import { demoCall } from '../agent/frontdesk/liveCall';
import './DiaryDemoSettings.css';

/**
 * Staging only: fill the diary with a believable day for screenshots, demos
 * and sales calls. Nothing is written to the salon. The diary renders the
 * made-up day instead of calling the API, and turning the demo off brings
 * the real book straight back.
 */
export default function DiaryDemoSettings(props) {
  const history = useHistory();
  const [cfg, setCfg] = useState(getDiaryDemo);
  const summary = demoSummary(cfg.trade, cfg.palette);

  useEffect(() => {
    const on = (e) => setCfg(e.detail || getDiaryDemo());
    window.addEventListener('beu:diary-demo-changed', on);
    return () => window.removeEventListener('beu:diary-demo-changed', on);
  }, []);

  const update = (patch) => setCfg(setDiaryDemo(patch));

  const fill = () => {
    update({ on: true });
    toast.success('Diary filled with demo bookings');
    history.push(`/diary/${localStorage.getItem('salonId') || ''}`);
  };

  const stop = () => {
    clearDiaryDemo();
    setCfg(getDiaryDemo());
    toast.success('Demo off. Your real diary is back.');
  };

  return (
    <SettingsPageLayout activeTab="diary-demo" {...props}>
      <div className="catalogue-content-wrapper ddm-page">
        <div className="ddm-banner">
          <b>Staging tool.</b> Hidden on customer builds. The bookings live on screen only, so nothing is saved, sent or charged, and no real booking is touched.
        </div>

        <div className="settings-card">
          <div className="settings-card-header ddm-head">
            <div>
              <h2>Bailey on a call</h2>
              <p>A pretend 40-second call, so Bailey picking up the handset can be shown on the desk pet and in the Bailey tab. Nothing is dialled.</p>
            </div>
          </div>
          <div className="ddm-actions">
            <button type="button" className="settings-btn settings-btn-primary" onClick={() => { demoCall(40); toast.success('Bailey is on a call for 40 seconds'); }}>Demo a call</button>
          </div>
        </div>

        <div className="settings-card">
          <div className="settings-card-header ddm-head">
            <div>
              <h2>Diary demo</h2>
              <p>A full, believable day for demos and screenshots.</p>
            </div>
            <span className={`settings-badge ${cfg.on ? 'settings-badge-info' : 'settings-badge-default'}`}>{cfg.on ? 'On' : 'Off'}</span>
          </div>

          <div className="ddm-label">Business type</div>
          <div className="ddm-trades">
            {TRADE_IDS.map((id) => (
              <button
                key={id}
                type="button"
                className={`ddm-trade${cfg.trade === id ? ' is-on' : ''}`}
                onClick={() => update({ trade: id })}
              >
                <b>{TRADES[id].label}</b>
                <span>{TRADES[id].blurb}</span>
                <small>{TRADES[id].staff.length} people · {TRADES[id].services.length} services</small>
              </button>
            ))}
          </div>

          <div className="ddm-label">Colour theme</div>
          <p className="ddm-hint">The same palettes you get when setting a service colour.</p>
          <div className="ddm-palettes">
            {PALETTE_NAMES.map((name) => {
              const swatches = demoSummary(cfg.trade, name).colours;
              return (
                <button
                  key={name}
                  type="button"
                  className={`ddm-palette${cfg.palette === name ? ' is-on' : ''}`}
                  onClick={() => update({ palette: name })}
                >
                  <span className="ddm-swatches">
                    {swatches.map((hex) => <i key={hex} style={{ background: hex }} />)}
                  </span>
                  <span className="ddm-palette-name">{name}</span>
                </button>
              );
            })}
          </div>

          <div className="ddm-label">How busy</div>
          <div className="ddm-density">
            {[['steady', 'Steady', 'Gaps between clients, room to book'], ['busy', 'Rammed', 'Back to back, barely a gap']].map(([id, label, blurb]) => (
              <button key={id} type="button" className={`ddm-dens${cfg.density === id ? ' is-on' : ''}`} onClick={() => update({ density: id })}>
                <b>{label}</b><span>{blurb}</span>
              </button>
            ))}
          </div>

          <div className="ddm-preview">
            <span>{summary.staff} columns, {summary.services} services, coloured in {cfg.palette}. Sundays are closed and the week fills out around the day you are on.</span>
          </div>

          <div className="ddm-actions">
            {cfg.on && <button type="button" className="settings-btn settings-btn-secondary" onClick={stop}>Turn the demo off</button>}
            <button type="button" className="settings-btn settings-btn-primary" onClick={fill}>{cfg.on ? 'Open the diary' : 'Fill the diary'}</button>
          </div>
        </div>

        <div className="settings-card">
          <div className="settings-card-header">
            <h2>What the demo changes</h2>
            <p>Worth knowing before you show it to anyone.</p>
          </div>
          <ul className="ddm-list">
            <li>The diary shows made-up staff, clients and bookings for the trade you pick, on every date you move to.</li>
            <li>The diary shows nothing to say it is a demo, so screenshots come out clean. Come back here to switch it off.</li>
            <li>Tapping a demo booking does nothing, because there is no record behind it.</li>
            <li>Everywhere else in Beu, including Clients, Finance and checkout, still shows your real data.</li>
          </ul>
        </div>
      </div>
    </SettingsPageLayout>
  );
}
